import { EntitiesTexts } from './EntitiesTexts';
import { ApiService } from '../../api/services/ApiService';
import { PageService } from '../../services/PageService.svelte';
import { UserService } from '../../services/UserService.svelte';
import type { DeletionPermission } from '../../components/actions/actions.types';
import type { EntitiesRightsBody, EntityCreateRights, OrganizationBody, PositionBody, RecordRights } from '../../api/types/entities.types';

/** A person with what decides its deletion — a list row (`user`) or a GET (`is_user`). */
export interface PersonDeletionCandidate {
    person_id?: number;
    is_user?: boolean | null;
    user?: unknown;
    rights?: RecordRights;
}

class EntitiesRightsState {
    public Create = $state<EntitiesRightsBody | null>(null);
}

/**
 * What the logged-in user may do in the Entities module — the one place pages, buttons and links of every module ask
 * (`EntitiesRights.canOpenPerson()`). A record brings its own `rights`; what can be created comes from
 * `GET /entities/rights`, asked only when needed and kept `CreateRightsMaxAgeMs`. The API stays the guard. A way to a
 * page is left out without the right, a forbidden action on a record stays visible, disabled, with the reason.
 */
class EntitiesRightsClass {
    private readonly CreateRightsMaxAgeMs = 5 * 60 * 1000;
    private readonly State = new EntitiesRightsState();
    private LoadedAt = 0;
    private LoadedFor: number | undefined = undefined;
    private IsLoading = false;

    /** The tags and rules for creating a record of the type, nothing until loaded. */
    public getCreateRights(resource: 'person' | 'organization' | 'intranet'): EntityCreateRights | null {
        this.refreshCreateRights();

        return this.State.Create?.[resource] ?? null;
    }

    public canEdit(rights: RecordRights | undefined): boolean {
        return rights?.write === true;
    }

    public canEditContacts(rights: RecordRights | undefined): boolean {
        this.refreshCreateRights();

        return rights?.read === true && (rights.write || this.State.Create?.contact.can_create === true);
    }

    // - persons

    public canCreatePerson(): boolean {
        return this.getCreateRights('person')?.can_create === true;
    }

    public canListPersons(): boolean {
        return PageService.canAccess('persons');
    }

    public canOpenPerson(): boolean {
        return PageService.canAccess('person');
    }

    public canSeePersonCard(): boolean {
        return PageService.hasAccess('user');
    }

    public getPersonDeletion(person: PersonDeletionCandidate | null | undefined): DeletionPermission {
        if (!person?.person_id || person.rights?.delete !== true) {
            return { allowed: false, label: EntitiesTexts.PersonDelete };
        }
        if (person.is_user || Boolean(person.user)) {
            return { allowed: false, label: EntitiesTexts.PersonIsUser };
        }

        return { allowed: true, label: EntitiesTexts.PersonDelete };
    }

    // - organizations

    public canCreateOrganization(): boolean {
        return this.getCreateRights('organization')?.can_create === true;
    }

    public canListOrganizations(): boolean {
        return PageService.canAccess('organizations');
    }

    public canOpenOrganization(): boolean {
        return PageService.canAccess('organization');
    }

    public canSeeOrganizationCard(): boolean {
        return PageService.hasAccess('user');
    }

    public getOrganizationDeletion(organization: (Partial<OrganizationBody> & { rights?: RecordRights }) | null | undefined): DeletionPermission {
        return { allowed: Boolean(organization?.organization_id) && organization?.rights?.delete === true, label: EntitiesTexts.OrganizationDelete };
    }

    // - positions: everybody reads them (choices), admins keep them

    public canEditPositions(): boolean {
        return PageService.hasAccess('admin');
    }

    public canListPositions(): boolean {
        return PageService.canAccess('positions');
    }

    public canOpenPosition(): boolean {
        return PageService.canAccess('position');
    }

    public getPositionDeletion(position: Partial<PositionBody> | null | undefined): DeletionPermission {
        if (!position || !PageService.hasAccess('admin')) {
            return { allowed: false, label: EntitiesTexts.PositionDelete };
        }
        if (position.is_used) {
            return { allowed: false, label: EntitiesTexts.PositionUsed };
        }

        return { allowed: true, label: EntitiesTexts.PositionDelete };
    }

    /** Loads when nothing is known for this user or the last answer is too old; the state updates once it is there. */
    private refreshCreateRights(): void {
        const UserId = UserService.body.user?.user_id;
        const IsFresh = this.LoadedFor === UserId && Date.now() - this.LoadedAt < this.CreateRightsMaxAgeMs;
        if (this.IsLoading || IsFresh || UserId === undefined) {
            return;
        }
        this.IsLoading = true;
        void ApiService.entities.getRights().then((result): void => {
            this.IsLoading = false;
            this.LoadedAt = Date.now();
            this.LoadedFor = UserId;
            this.State.Create = result.body ?? null;
        });
    }
}

export const EntitiesRights = new EntitiesRightsClass();
